// ─── Shared helpers for Quick Hours <-> From/To conversion ───────────────────

import { getWeekNumber, weekToDateRange } from './utils';

export const DEFAULT_START_HOUR = '09:00';
export const MIN_DAILY_HOURS = 8;
export const MAX_DAILY_HOURS = 8;

/**
 * The one project routed to the employee's direct manager, and the only one
 * exempt from the future-date policy and the note guardrails.
 * Mirrors backend shared/constants.ts — keep both in sync.
 */
export const NON_PROJECT_RELATED_ID = '00000000-0000-4000-8000-000000000001';

/** True for the one project routed to the employee's direct manager. */
export const routesToDirectManager = (projectId: string | undefined): boolean =>
  projectId === NON_PROJECT_RELATED_ID;

// ─── Future-date policy ──────────────────────────────────────────────────────
// Non-project time can be logged for any future date (planned leave, training,
// etc.). Project work — billable or not — can only be logged ahead within the
// ongoing week, never past it. Mirrors backend shared/utils.ts — keep in sync.

/** Saturday closing the Sun–Sat week containing `dateStr`, as YYYY-MM-DD. */
export function weekEndOf(dateStr: string): string {
  return weekToDateRange(Number(dateStr.slice(0, 4)), getWeekNumber(dateStr)).to;
}

/**
 * Why `dateStr` can't be logged yet, or null when it's allowed. Compares
 * YYYY-MM-DD strings directly — they sort chronologically.
 */
export function futureDateError(
  dateStr: string,
  allowsFutureDates: boolean,
  now: Date = new Date(),
): string | null {
  const today = now.toISOString().slice(0, 10);
  if (dateStr <= today) return null; // today or earlier is always fine
  if (allowsFutureDates) return null;
  const weekEnd = weekEndOf(today);
  if (dateStr <= weekEnd) return null;
  return `Project hours can only be logged up to the end of the current week (${weekEnd}) — ${dateStr} is later.`;
}

/** Converts 'HH:MM' to minutes since midnight */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
}

/** Converts minutes since midnight to 'HH:MM' (wraps at 24h) */
export function minutesToTime(totalMinutes: number): string {
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Quick Hours mode: given a number of hours, returns { fromHour, toHour } starting at 09:00 */
export function quickHoursToRange(hours: number): { fromHour: string; toHour: string } {
  const fromMinutes = timeToMinutes(DEFAULT_START_HOUR);
  const toMinutes = fromMinutes + Math.round(hours * 60);
  return {
    fromHour: DEFAULT_START_HOUR,
    toHour: minutesToTime(toMinutes),
  };
}

/**
 * Quick Hours mode, sequential variant: given a number of hours and an explicit
 * start time (e.g. the end time of the previous task that day), returns
 * { fromHour, toHour } starting at that time instead of always at 09:00.
 */
export function quickHoursToRangeFrom(hours: number, startHour: string): { fromHour: string; toHour: string } {
  const fromMinutes = timeToMinutes(startHour);
  const toMinutes = fromMinutes + Math.round(hours * 60);
  return {
    fromHour: startHour,
    toHour: minutesToTime(toMinutes),
  };
}

/**
 * From/To mode: given fromHour and toHour, returns the decimal hours worked.
 * Midnight-aware: if toHour <= fromHour, the range is treated as crossing into
 * the next day (e.g. 22:00 -> 02:00 = 4h), not a negative/invalid duration.
 * An identical fromHour/toHour is treated as zero duration, not a full 24h.
 */
export function rangeToHours(fromHour: string, toHour: string): number {
  if (fromHour === toHour) return 0;
  let diff = timeToMinutes(toHour) - timeToMinutes(fromHour);
  if (diff < 0) diff += 1440;
  return Math.round((diff / 60) * 100) / 100; // round to 2 decimals
}

export const HALF_HOUR_ERROR = 'Hours must be in increments of 0.5 (e.g. 6 or 6.5).';

/** True when a duration is a whole number of half hours — mirrors the backend rule. */
export function isHalfHourStep(hours: number): boolean {
  return Math.round(hours * 60) % 30 === 0;
}

/**
 * Sums hours already logged for a given date from a list of entries,
 * optionally excluding one entry (the one currently being edited).
 */
export function sumHoursForDate<T extends { date: string; hours: number; id?: string }>(
  entries: T[],
  date: string,
  excludeId?: string
): number {
  return entries
    .filter((e) => e.date.slice(0, 10) === date && e.id !== excludeId)
    .reduce((sum, e) => sum + e.hours, 0);
}

/** Returns a warning message if total daily hours (existing + new) is outside the allowed range, else null */
export function getDailyHoursWarning(existingTotal: number, newHours: number): string | null {
  const total = Math.round((existingTotal + newHours) * 100) / 100;
  if (total > MAX_DAILY_HOURS) {
    return `Total logged for this day would be ${total}h. Max ${MAX_DAILY_HOURS}h per day.`;
  }
  if (total < MIN_DAILY_HOURS) {
    return `Total logged for this day is ${total}h. At least ${MIN_DAILY_HOURS}h required.`;
  }
  return null;
}

// ─── Overlap detection (midnight-aware) ───────────────────────────────────────

type TimeSegment = { start: number; end: number };

/**
 * Splits a 'HH:MM'-'HH:MM' range into one or two non-wrapping minute segments.
 * A range is treated as wrapping past midnight whenever toHour <= fromHour
 * (e.g. 23:00 -> 01:00). Same-instant ranges (from === to) produce no segments.
 */
function toSegments(fromHour: string, toHour: string): TimeSegment[] {
  const from = timeToMinutes(fromHour);
  const to = timeToMinutes(toHour);
  if (from === to) return [];
  if (to > from) {
    return [{ start: from, end: to }];
  }
  // Wraps past midnight: split into [from, 1440) and [0, to]
  return [
    { start: from, end: 1440 },
    { start: 0, end: to },
  ];
}

function segmentsOverlap(a: TimeSegment, b: TimeSegment): boolean {
  return a.start < b.end && b.start < a.end;
}

/** True if the two HH:MM ranges overlap, accounting for ranges that wrap past midnight. */
export function rangesOverlap(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  const segmentsA = toSegments(aFrom, aTo);
  const segmentsB = toSegments(bFrom, bTo);
  for (const segA of segmentsA) {
    for (const segB of segmentsB) {
      if (segmentsOverlap(segA, segB)) return true;
    }
  }
  return false;
}

/**
 * Returns the first entry on `date` (excluding `excludeId`) whose fromHour/toHour
 * overlaps with the given range, or null if there's no conflict.
 */
export function findOverlappingEntry<
  T extends { date: string; fromHour: string; toHour: string; id?: string }
>(entries: T[], date: string, fromHour: string, toHour: string, excludeId?: string): T | null {
  const sameDay = entries.filter((e) => e.date.slice(0, 10) === date && e.id !== excludeId);
  for (const entry of sameDay) {
    if (rangesOverlap(fromHour, toHour, entry.fromHour, entry.toHour)) {
      return entry;
    }
  }
  return null;
}

/**
 * Returns the start time the next task on `date` should use: the toHour of
 * whichever same-day entry has the latest end time, or DEFAULT_START_HOUR if
 * there are no entries yet for that date.
 */
export function getNextStartTime<T extends { date: string; fromHour: string; toHour: string }>(
  entries: T[],
  date: string
): string {
  const sameDay = entries.filter((e) => e.date.slice(0, 10) === date);
  if (sameDay.length === 0) return DEFAULT_START_HOUR;

  let latest = sameDay[0].toHour;
  for (const entry of sameDay) {
    if (timeToMinutes(entry.toHour) > timeToMinutes(latest)) {
      latest = entry.toHour;
    }
  }
  return latest;
}

export const OVERLAP_ERROR_MESSAGE = "You can't have two overlapping entries.";
